package org.teddapp.project.ui

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import com.tedd.db.User_profile
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch
import org.teddapp.project.db.repo.PreferencesRepository
import org.teddapp.project.db.repo.UserProfileRepository

enum class Language(val code: String, val label: String) {
    French("fr", "Français"),
    English("en", "English"),
}

// ── Préférences device-only (clé/valeur, PreferencesRepository) ────────────
// Ces préférences NE SONT PAS synchronisées entre appareils car elles n'ont
// pas de sens cross-device (creds, biométrie, scheduler, UI cachée local).
private const val KEY_HIDE_BALANCE = "hide_balance"
private const val PANE_PREFIX = "pane."
private const val KEY_BIOMETRIC_ENABLED = "biometric_enabled"
private const val KEY_SYNC_ENABLED = "sync_enabled"
private const val KEY_SYNC_LAST_AT = "sync_last_at"
private const val KEY_SUPABASE_URL = "supabase_url"
private const val KEY_SUPABASE_ANON_KEY = "supabase_anon_key"
private const val KEY_BACKUP_AUTO = "backup_auto"
private const val KEY_BACKUP_FREQUENCY = "backup_frequency"   // daily|weekly|twice_monthly|monthly
const val DEFAULT_BACKUP_FREQUENCY = "twice_monthly"

// Valeurs par défaut pour le profil utilisateur (utilisées si user_profile vide).
const val DEFAULT_USER_NAME = "Léa"
const val DEFAULT_FIN_PERIOD = "all"      // all | today | 7d | 30d | month | lastmonth
const val DEFAULT_FIN_CURRENCY = "XOF"    // XOF | USD | EUR
const val DEFAULT_POMO_FOCUS = 25
const val DEFAULT_POMO_SHORT = 5
const val DEFAULT_POMO_LONG = 15

/**
 * Préférences utilisateur (état Compose réactif + persistance SQLite).
 *
 * Deux sources de persistance distinctes :
 *  1. **`userProfileRepo`** (table singleton `user_profile`, synchronisée) :
 *     fullname, email, avatarUrl, bio, language, themeMode, finance default,
 *     pomodoro durations, notifications. C'est l'identité + les préférences
 *     applicatives portables entre desktop et mobile.
 *  2. **`prefsRepo`** (table key/value `app_preference`, NON synchronisée) :
 *     supabaseUrl/Key (sécurité), biometric, backup auto/freq (per device),
 *     syncEnabled/LastAt (per device), hideBalance (UI per device).
 *
 * Les `apply*` mettent à jour l'état Compose ET persistent dans la bonne table.
 * Le caller doit ensuite déclencher `observeUserProfile()` côté top-level pour
 * que les changements pull-syncés (depuis un autre device) repassent dans l'état.
 */
class AppSettings(
    private val prefsRepo: PreferencesRepository? = null,
    private val userProfileRepo: UserProfileRepository? = null,
    initialPrefs: Map<String, String> = emptyMap(),
    initialProfile: User_profile? = null,
) {

    // ── Profil utilisateur (synced) ───────────────────────────────────
    var fullname: String by mutableStateOf(initialProfile?.fullname ?: "")
        private set
    var email: String by mutableStateOf(initialProfile?.email ?: "")
        private set
    var avatarUrl: String by mutableStateOf(initialProfile?.avatar_url ?: "")
        private set
    var bio: String by mutableStateOf(initialProfile?.bio ?: "")
        private set

    /** Alias de fullname pour le code existant. Fallback "Léa" si vide. */
    val userName: String get() = fullname.ifBlank { DEFAULT_USER_NAME }

    // Encre est d'abord un cahier de papier : le clair est le défaut, pas le thème système.
    var themeMode: ThemeMode by mutableStateOf(
        initialProfile?.theme_mode
            ?.let { runCatching { ThemeMode.valueOf(it) }.getOrNull() }
            ?: ThemeMode.Light
    )
        private set

    var language: Language by mutableStateOf(
        initialProfile?.language
            ?.let { code -> Language.entries.firstOrNull { it.code == code } }
            ?: Language.French
    )
        private set

    var financeDefaultPeriod: String by mutableStateOf(
        initialProfile?.finance_default_period ?: DEFAULT_FIN_PERIOD
    )
        private set

    var financeDefaultCurrency: String by mutableStateOf(
        initialProfile?.finance_default_currency ?: DEFAULT_FIN_CURRENCY
    )
        private set

    var pomodoroFocusMin: Int by mutableStateOf(
        (initialProfile?.pomodoro_focus_min ?: DEFAULT_POMO_FOCUS.toLong()).toInt()
    )
        private set

    var pomodoroShortBreakMin: Int by mutableStateOf(
        (initialProfile?.pomodoro_short_break_min ?: DEFAULT_POMO_SHORT.toLong()).toInt()
    )
        private set

    var pomodoroLongBreakMin: Int by mutableStateOf(
        (initialProfile?.pomodoro_long_break_min ?: DEFAULT_POMO_LONG.toLong()).toInt()
    )
        private set

    var notifyHabits: Boolean by mutableStateOf((initialProfile?.notify_habits ?: 1L) == 1L)
        private set
    var notifyDeadlines: Boolean by mutableStateOf((initialProfile?.notify_deadlines ?: 1L) == 1L)
        private set
    var notifyPomodoroEnd: Boolean by mutableStateOf((initialProfile?.notify_pomodoro_end ?: 1L) == 1L)
        private set
    var notifyBudgetAlerts: Boolean by mutableStateOf((initialProfile?.notify_budget_alerts ?: 1L) == 1L)
        private set

    // ── Préférences device-only (PreferencesRepository) ───────────────
    var hideBalance: Boolean by mutableStateOf(initialPrefs[KEY_HIDE_BALANCE] == "true")
        private set

    var biometricEnabled: Boolean by mutableStateOf(initialPrefs[KEY_BIOMETRIC_ENABLED] == "true")
        private set

    var syncEnabled: Boolean by mutableStateOf(initialPrefs[KEY_SYNC_ENABLED] == "true")
        private set
    var syncLastAt: Long by mutableStateOf(initialPrefs[KEY_SYNC_LAST_AT]?.toLongOrNull() ?: 0L)
        private set
    var supabaseUrl: String by mutableStateOf(initialPrefs[KEY_SUPABASE_URL] ?: "")
        private set
    var supabaseAnonKey: String by mutableStateOf(initialPrefs[KEY_SUPABASE_ANON_KEY] ?: "")
        private set

    var backupAuto: Boolean by mutableStateOf(initialPrefs[KEY_BACKUP_AUTO] != "false")
        private set
    var backupFrequency: String by mutableStateOf(initialPrefs[KEY_BACKUP_FREQUENCY] ?: DEFAULT_BACKUP_FREQUENCY)
        private set

    private val scope = CoroutineScope(Dispatchers.Default + SupervisorJob())

    // ──────────────────────────────────────────────────────────────────
    // Profil : push (local → DB) et adopt (DB pull → local state)
    // ──────────────────────────────────────────────────────────────────

    /** Push l'état Compose courant vers la table user_profile. Idempotent. */
    private fun pushProfile() {
        val upr = userProfileRepo ?: return
        scope.launch {
            upr.update(
                fullname = fullname,
                email = email,
                avatarUrl = avatarUrl,
                bio = bio,
                language = language.code,
                themeMode = themeMode.name,
                financeDefaultPeriod = financeDefaultPeriod,
                financeDefaultCurrency = financeDefaultCurrency,
                pomodoroFocusMin = pomodoroFocusMin.toLong(),
                pomodoroShortBreakMin = pomodoroShortBreakMin.toLong(),
                pomodoroLongBreakMin = pomodoroLongBreakMin.toLong(),
                notifyHabits = if (notifyHabits) 1L else 0L,
                notifyDeadlines = if (notifyDeadlines) 1L else 0L,
                notifyPomodoroEnd = if (notifyPomodoroEnd) 1L else 0L,
                notifyBudgetAlerts = if (notifyBudgetAlerts) 1L else 0L,
            )
        }
    }

    /**
     * Adopte un profil reçu depuis le pull sync (ou recharge initiale).
     * Met à jour uniquement les champs qui diffèrent pour éviter les recomposes inutiles.
     */
    fun adoptProfile(p: User_profile) {
        if (fullname != p.fullname) fullname = p.fullname
        if (email != p.email) email = p.email
        if (avatarUrl != p.avatar_url) avatarUrl = p.avatar_url
        if (bio != p.bio) bio = p.bio
        Language.entries.firstOrNull { it.code == p.language }?.let { if (language != it) language = it }
        runCatching { ThemeMode.valueOf(p.theme_mode) }.getOrNull()?.let { if (themeMode != it) themeMode = it }
        if (financeDefaultPeriod != p.finance_default_period) financeDefaultPeriod = p.finance_default_period
        if (financeDefaultCurrency != p.finance_default_currency) financeDefaultCurrency = p.finance_default_currency
        if (pomodoroFocusMin.toLong() != p.pomodoro_focus_min) pomodoroFocusMin = p.pomodoro_focus_min.toInt()
        if (pomodoroShortBreakMin.toLong() != p.pomodoro_short_break_min) pomodoroShortBreakMin = p.pomodoro_short_break_min.toInt()
        if (pomodoroLongBreakMin.toLong() != p.pomodoro_long_break_min) pomodoroLongBreakMin = p.pomodoro_long_break_min.toInt()
        val nh = p.notify_habits == 1L; if (notifyHabits != nh) notifyHabits = nh
        val nd = p.notify_deadlines == 1L; if (notifyDeadlines != nd) notifyDeadlines = nd
        val np = p.notify_pomodoro_end == 1L; if (notifyPomodoroEnd != np) notifyPomodoroEnd = np
        val nb = p.notify_budget_alerts == 1L; if (notifyBudgetAlerts != nb) notifyBudgetAlerts = nb
    }

    // ──────────────────────────────────────────────────────────────────
    // Setters profil (synced)
    // ──────────────────────────────────────────────────────────────────

    fun applyFullname(name: String) {
        val clean = name.trim()
        fullname = clean
        pushProfile()
    }

    /** Alias historique — pointe vers applyFullname. */
    fun applyUserName(name: String) = applyFullname(name)

    fun applyEmail(value: String) {
        email = value.trim()
        pushProfile()
    }

    fun applyAvatarUrl(value: String) {
        avatarUrl = value.trim()
        pushProfile()
    }

    fun applyBio(value: String) {
        bio = value.trim()
        pushProfile()
    }

    fun applyThemeMode(mode: ThemeMode) {
        themeMode = mode
        pushProfile()
    }

    fun applyLanguage(lang: Language) {
        language = lang
        pushProfile()
    }

    fun applyFinanceDefaultPeriod(period: String) {
        financeDefaultPeriod = period
        pushProfile()
    }

    fun applyFinanceDefaultCurrency(currency: String) {
        financeDefaultCurrency = currency
        pushProfile()
    }

    fun applyPomodoroFocusMin(min: Int) {
        pomodoroFocusMin = min
        pushProfile()
    }

    fun applyPomodoroShortBreakMin(min: Int) {
        pomodoroShortBreakMin = min
        pushProfile()
    }

    fun applyPomodoroLongBreakMin(min: Int) {
        pomodoroLongBreakMin = min
        pushProfile()
    }

    fun applyNotifyHabits(on: Boolean) {
        notifyHabits = on
        pushProfile()
    }

    fun applyNotifyDeadlines(on: Boolean) {
        notifyDeadlines = on
        pushProfile()
    }

    fun applyNotifyPomodoroEnd(on: Boolean) {
        notifyPomodoroEnd = on
        pushProfile()
    }

    fun applyNotifyBudgetAlerts(on: Boolean) {
        notifyBudgetAlerts = on
        pushProfile()
    }

    // ──────────────────────────────────────────────────────────────────
    // Setters device-only (prefsRepo)
    // ──────────────────────────────────────────────────────────────────

    /**
     * Largeur des panneaux latéraux, par nom : un nombre de dp, ou « reduit ». Propre à
     * l'appareil : un écran large et un petit portable n'ont pas les mêmes besoins.
     */
    private val panes = androidx.compose.runtime.mutableStateMapOf<String, String>().apply {
        initialPrefs.forEach { (k, v) -> if (k.startsWith(PANE_PREFIX)) put(k.removePrefix(PANE_PREFIX), v) }
    }

    fun pane(name: String): String? = panes[name]

    fun applyPane(name: String, value: String?) {
        if (value == null) panes.remove(name) else panes[name] = value
        prefsRepo?.let { scope.launch { if (value == null) it.set(PANE_PREFIX + name, "") else it.set(PANE_PREFIX + name, value) } }
    }

    fun applyHideBalance(hide: Boolean) {
        hideBalance = hide
        prefsRepo?.let { scope.launch { it.set(KEY_HIDE_BALANCE, hide.toString()) } }
    }

    fun applyBiometricEnabled(on: Boolean) {
        biometricEnabled = on
        prefsRepo?.let { scope.launch { it.set(KEY_BIOMETRIC_ENABLED, on.toString()) } }
    }

    fun applySyncEnabled(on: Boolean) {
        syncEnabled = on
        prefsRepo?.let { scope.launch { it.set(KEY_SYNC_ENABLED, on.toString()) } }
    }

    fun applySyncLastAt(epochMs: Long) {
        syncLastAt = epochMs
        prefsRepo?.let { scope.launch { it.set(KEY_SYNC_LAST_AT, epochMs.toString()) } }
    }

    fun applySupabaseUrl(url: String) {
        supabaseUrl = url
        prefsRepo?.let { scope.launch { it.set(KEY_SUPABASE_URL, url) } }
    }

    fun applySupabaseAnonKey(key: String) {
        supabaseAnonKey = key
        prefsRepo?.let { scope.launch { it.set(KEY_SUPABASE_ANON_KEY, key) } }
    }

    fun applyBackupAuto(on: Boolean) {
        backupAuto = on
        prefsRepo?.let { scope.launch { it.set(KEY_BACKUP_AUTO, on.toString()) } }
    }

    fun applyBackupFrequency(freq: String) {
        backupFrequency = freq
        prefsRepo?.let { scope.launch { it.set(KEY_BACKUP_FREQUENCY, freq) } }
    }
}

val LocalAppSettings = staticCompositionLocalOf<AppSettings> {
    error("AppSettings non fourni — englober dans un CompositionLocalProvider(LocalAppSettings provides …).")
}
